import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import routeConfiguration from '../../../../routeConfiguration';
import { neutral, petrol } from '../../../../styles/mui/colors';
import useStoryblokImage from '../../../../util/hooks/useStoryblokImage';
import { createResourceLocatorString } from '../../../../util/routes';
import ProgressiveImage from '../../../Image/ProgressiveImage';
import Link from '../../../Links/Link/Link';

export const StoryblokArchiveSkeletonPostTile = () => {
  return (
    <Card sx={{ width: '100%', height: '100%' }}>
      <CardMedia sx={{ height: 280 }}>
        <Skeleton sx={{ height: 280 }} variant="rectangular" />
      </CardMedia>
      <CardContent>
        <Skeleton sx={{ height: 60, mb: 2 }} variant="rectangular" />
        <Skeleton sx={{ height: 80, mb: 2 }} variant="rectangular" />
        <Skeleton sx={{ height: 20 }} variant="rectangular" />
      </CardContent>
    </Card>
  );
};

const cardStyle = {
  border: `1px solid ${neutral[200]}`,
  boxShadow: 0,
  p: 2,
  borderRadius: 1.1,
};

const cardMediaStyle = { borderRadius: 1, height: 280, overflow: 'hidden' };

const cardContentStyle = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '&:last-child': { px: 0, pt: 2, pb: 0 },
};

const chipStyle = {
  mr: 1,
  bgcolor: neutral[50],
  color: petrol.main,
};

/**
 * @param {object} props - props
 * @param {object} props.heroImage - hero image
 * @param {string} props.slug - slug
 * @param {string} props.publishedAt - published at
 * @param {object} props.metatags - metatags
 * @param {object} props.content - content
 * @returns {JSX.Element}
 */

const StoryblokArchivePostTile = ({
  heroImage = null,
  slug = '',
  publishedAt = '',
  metatags = {
    title: 'No title',
    description: 'No Description',
  },
  content = { tags: '' },
}) => {
  const { title, description } = metatags;

  // Split the tags string and filter out any empty strings
  const tags =
    typeof content.tags === 'string'
      ? content.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter((tag) => tag !== '')
      : [];

  const intl = useIntl();
  const { sources, blurSources, src, blurSrc } = useStoryblokImage(heroImage?.filename, {
    size: 640,
    blurSize: 320,
  });

  const cardMedia = heroImage ? (
    <CardMedia component="picture" sx={cardMediaStyle} title={heroImage.title}>
      <ProgressiveImage
        alt={heroImage.alt}
        blurSources={blurSources}
        blurSrc={blurSrc}
        height="280px"
        sources={sources}
        src={src}
        title={heroImage.title}
      />
    </CardMedia>
  ) : (
    <Box
      height={280}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'grey.200',
      }}
    >
      <FormattedMessage id="BlogCard.heroImageMissing" />
    </Box>
  );

  const titleLabel = title ? title : intl.formatMessage({ id: 'BlogCard.titleMissing' });
  const descriptionLabel = description
    ? description
    : intl.formatMessage({ id: 'BlogCard.descriptionMissing' });

  const cardContent = (
    <CardContent sx={cardContentStyle}>
      <Typography component="h2" gutterBottom mt={0} variant="h5">
        {titleLabel}
      </Typography>
      <Typography color="text.secondary" variant="body1">
        {descriptionLabel}
      </Typography>
      <Typography color={neutral[400]} variant="subline2">
        {intl.formatDate(new Date(publishedAt), {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })}
      </Typography>
      <Box mt={1} width={1}>
        {tags.length > 0
          ? tags.map((tag, index) => <Chip key={index} label={tag} sx={chipStyle} />)
          : null}
      </Box>
    </CardContent>
  );

  return (
    <Box
      sx={{
        p: 1,
        transition: 'transform 0.3s', // Smooth transition for scaling
        '&:hover': {
          transform: 'scale(1.02)', // Scale up the card slightly on hover
        },
      }}
    >
      <Link
        sx={{
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'none',
          },
        }}
        to={createResourceLocatorString(
          'StoryblokPage',
          routeConfiguration(),
          { slug: 'blog', rest: slug },
          {},
          intl.locale
        )}
      >
        <Card sx={cardStyle}>
          {cardMedia}
          {cardContent}
        </Card>
      </Link>
    </Box>
  );
};

export default StoryblokArchivePostTile;
